// Drill-through URL builders — FE-031 / spec §7.0.12.
//
// Centralised so every dashboard widget produces the same URL shape
// for the same logical click. The FE-032..FE-037 widgets call into
// these helpers; receivers (Commitment Tracker, Account list, Account
// detail) read the same query params on mount.
//
// Convention:
//   * Booleans serialise as ``"true"`` / ``"false"`` so the receiver
//     does not have to branch per key.
//   * Undefined / null / empty values are dropped — a fresh dashboard
//     never produces ``?assigned_to=`` with an empty value.
//   * Query keys come out sorted alphabetically for deterministic
//     output (snapshot-friendly tests, deep-linkable URLs).

import type { AccountFilters, CommitmentFilters } from "./types";

const COMMITMENT_TRACKER_PATH = "/dashboard/commitment-tracker";
const ACCOUNTS_PATH = "/dashboard/accounts";

type Primitive = string | number | boolean;

function appendParam(
  sp: URLSearchParams,
  key: string,
  value: Primitive | undefined | null,
): void {
  if (value === undefined || value === null || value === "") return;
  if (typeof value === "boolean") {
    sp.append(key, value ? "true" : "false");
    return;
  }
  sp.append(key, String(value));
}

function emit(path: string, sp: URLSearchParams): string {
  // Sort keys for deterministic output. URLSearchParams keeps insert
  // order, so we reseed a new instance from the sorted entries.
  const entries = Array.from(sp.entries()).sort(
    ([a], [b]) => (a < b ? -1 : a > b ? 1 : 0),
  );
  if (entries.length === 0) return path;
  const sorted = new URLSearchParams();
  for (const [k, v] of entries) sorted.append(k, v);
  return `${path}?${sorted.toString()}`;
}

// ── Public builders ─────────────────────────────────────────────────────

export interface DateRangeBoundary {
  startDate?: string;
  endDate?: string;
}

/**
 * Build a Commitment Tracker URL with the supplied filters and an
 * optional dashboard date range carried through.
 *
 * Used by every dashboard widget whose click target lives in the
 * Commitment Tracker (most overdue, employee performance,
 * client/account performance, CFD click-through, blocking-bar).
 */
export function buildCommitmentFilterUrl(
  filters: CommitmentFilters,
  dateRange?: DateRangeBoundary,
): string {
  const sp = new URLSearchParams();
  appendParam(sp, "assigned_to", filters.assigned_to);
  appendParam(sp, "client_id", filters.client_id);
  appendParam(sp, "plan_year_id", filters.plan_year_id);
  appendParam(sp, "status", filters.status);
  appendParam(sp, "overdue", filters.overdue);
  appendParam(sp, "has_due_date_time", filters.has_due_date_time);
  appendParam(sp, "has_assignee", filters.has_assignee);
  appendParam(sp, "has_complexity", filters.has_complexity);
  appendParam(sp, "has_reviewer", filters.has_reviewer);
  appendParam(sp, "is_blocking", filters.is_blocking);
  appendParam(sp, "is_blocked", filters.is_blocked);
  appendParam(sp, "blocked_by", filters.blocked_by);
  appendParam(sp, "has_description", filters.has_description);
  appendParam(sp, "risk_level", filters.risk_level);
  appendParam(sp, "on_time", filters.on_time);
  appendParam(sp, "category", filters.category);
  appendParam(sp, "snapshot_date", filters.snapshot_date);
  appendParam(sp, "start_date", filters.start_date ?? dateRange?.startDate);
  appendParam(sp, "end_date", filters.end_date ?? dateRange?.endDate);
  return emit(COMMITMENT_TRACKER_PATH, sp);
}

/**
 * Build an Account-surface URL. The `id` variant routes to the
 * account detail conditional render on `/dashboard/accounts`; every
 * other shape lands on the filtered list.
 */
export function buildAccountFilterUrl(
  filters: AccountFilters,
  dateRange?: DateRangeBoundary,
): string {
  const sp = new URLSearchParams();
  appendParam(sp, "id", filters.id);
  appendParam(sp, "risk_level", filters.risk_level);
  appendParam(sp, "has_overdue", filters.has_overdue);
  appendParam(sp, "going_cold", filters.going_cold);
  appendParam(sp, "renewal_month", filters.renewal_month);
  appendParam(sp, "renewal_status", filters.renewal_status);
  appendParam(sp, "renewal_attention", filters.renewal_attention);
  appendParam(sp, "start_date", filters.start_date ?? dateRange?.startDate);
  appendParam(sp, "end_date", filters.end_date ?? dateRange?.endDate);
  return emit(ACCOUNTS_PATH, sp);
}

/**
 * Translate a dashboard date range into the URL params drill-throughs
 * propagate. Pure helper; callers either spread it into the filter
 * object or pass the original `DateRangeBoundary` value through.
 */
export function buildDashboardDateParams(
  range: DateRangeBoundary,
): { start_date?: string; end_date?: string } {
  const out: { start_date?: string; end_date?: string } = {};
  if (range.startDate) out.start_date = range.startDate;
  if (range.endDate) out.end_date = range.endDate;
  return out;
}

// ── KPI-card / dashboard-card helpers ───────────────────────────────────

/**
 * Common gap-style KPI card mappings (`KPI_FILTER_MAP` from spec §7.0.12).
 * Each card key maps to the Commitment-tracker filter the card represents.
 */
export const KPI_FILTER_MAP: Record<string, CommitmentFilters> = {
  // "Commitments missing deadline" → has_due_date_time=false
  missing_deadline: { has_due_date_time: false },
  // "Commitments missing assignee" → has_assignee=false
  missing_assignee: { has_assignee: false },
  // "Commitments missing complexity" → has_complexity=false
  missing_complexity: { has_complexity: false },
  // "Commitments missing reviewer" → has_reviewer=false
  missing_reviewer: { has_reviewer: false },
};

export function getKpiCardFilterUrl(
  cardKey: keyof typeof KPI_FILTER_MAP,
  dateRange?: DateRangeBoundary,
): string {
  return buildCommitmentFilterUrl(KPI_FILTER_MAP[cardKey], dateRange);
}

// ── CFD click-through ──────────────────────────────────────────────────

export interface CfdDrillThroughInput {
  status: string;
  /** Snapshot date for the clicked band. */
  snapshotDate: string;
  /** Optional CFD filters carried through. */
  category?: string;
  assignee?: string;
}

export function buildCfdDrillThroughUrl(
  input: CfdDrillThroughInput,
  dateRange?: DateRangeBoundary,
): string {
  return buildCommitmentFilterUrl(
    {
      status: input.status,
      snapshot_date: input.snapshotDate,
      category: input.category,
      assigned_to: input.assignee,
    },
    dateRange,
  );
}

export const DRILL_THROUGH_PATHS = {
  commitmentTracker: COMMITMENT_TRACKER_PATH,
  accounts: ACCOUNTS_PATH,
} as const;
